/**
 * BulkImportStudentsDialog — upload a CSV to mass-onboard students.
 *
 * Flow:
 * 1) User picks a session (defaults to active session).
 * 2) Optionally downloads sample.csv to see expected headers.
 * 3) Selects a .csv file → POST /api/school/students/bulk-import
 * 4) Server returns { total_rows, created, skipped, errors, skipped_rows, error_rows }
 * 5) We show a result panel with counters + scrollable error/skip details.
 */
import React, { useState, useRef, useCallback } from "react";
import {
  UploadSimple,
  FileCsv,
  CheckCircle,
  Warning,
  XCircle,
  DownloadSimple,
} from "@phosphor-icons/react";
import api, { API_BASE, formatApiError, tokenStore } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function BulkImportStudentsDialog({
  open,
  onOpenChange,
  sessions,
  defaultSessionId,
  onImported,
}) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [sessionId, setSessionId] = useState(defaultSessionId || "");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Re-sync default session when prop changes / dialog re-opens
  React.useEffect(() => {
    if (open) {
      setFile(null);
      setResult(null);
      setError("");
      setSessionId(defaultSessionId || "");
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open, defaultSessionId]);

  const downloadSample = useCallback(async () => {
    try {
      const token = tokenStore.get();
      const res = await fetch(`${API_BASE}/school/students/bulk-import/sample.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "students-sample.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download sample CSV");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Choose a CSV file");
      return;
    }
    if (!sessionId) {
      setError("Pick a session");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/school/students/bulk-import?session_id=${encodeURIComponent(sessionId)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(data);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} student${data.created === 1 ? "" : "s"}`);
      } else {
        toast.message(`No new students. ${data.skipped} skipped, ${data.errors} errors.`);
      }
      onImported?.(data);
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UploadSimple size={20} weight="duotone" /> Bulk import students
          </DialogTitle>
          <DialogDescription>
            Upload a CSV to onboard many students at once. Required columns:
            <span className="font-mono text-[12px] ml-1">name, admission_no, class_name</span>.
            Optional: section, roll_no, gender, dob, parent_name, parent_phone, address.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Academic session *</Label>
              <Select value={sessionId} onValueChange={setSessionId}>
                <SelectTrigger className="mt-1.5" data-testid="bulk-import-session-select">
                  <SelectValue placeholder="Pick a session" />
                </SelectTrigger>
                <SelectContent>
                  {sessions.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`bulk-import-session-${s.id}`}>
                      {s.name}
                      {s.is_active ? <span className="text-emerald-600 ml-2">· active</span> : null}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 mt-1">
                Students will be created under classes that exist in this session. Rows with unknown
                class names will be reported as errors.
              </div>
            </div>

            <div className="border border-dashed border-slate-300 rounded-md p-5 bg-slate-50">
              <div className="flex items-center gap-3">
                <FileCsv size={32} weight="duotone" className="text-slate-500" />
                <div className="flex-1 min-w-0">
                  <Label className="font-medium">CSV file</Label>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="block text-sm text-slate-700 mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-blue-900 file:text-white hover:file:bg-blue-800 file:text-xs file:font-semibold"
                    data-testid="bulk-import-file-input"
                  />
                  {file && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      {file.name} · {(file.size / 1024).toFixed(1)} KB
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={downloadSample}
                  className="gap-1.5 shrink-0"
                  data-testid="bulk-import-sample-btn"
                >
                  <DownloadSimple size={14} /> Sample CSV
                </Button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
                {error}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting || !file || !sessionId}
                className="bg-blue-900 hover:bg-blue-800"
                data-testid="bulk-import-submit-btn"
              >
                {submitting ? "Importing…" : "Import students"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4" data-testid="bulk-import-result">
            <div className="grid grid-cols-3 gap-2">
              <ResultCounter
                label="Created"
                value={result.created}
                tone="emerald"
                icon={<CheckCircle size={18} weight="fill" />}
              />
              <ResultCounter
                label="Skipped"
                value={result.skipped}
                tone="amber"
                icon={<Warning size={18} weight="fill" />}
              />
              <ResultCounter
                label="Errors"
                value={result.errors}
                tone="red"
                icon={<XCircle size={18} weight="fill" />}
              />
            </div>
            <div className="text-xs text-slate-500">
              Processed <strong>{result.total_rows}</strong> row{result.total_rows === 1 ? "" : "s"}
              {result.session_name ? ` for session ${result.session_name}` : ""}.
            </div>

            {result.error_rows?.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Errors</div>
                <div className="border border-red-200 bg-red-50 rounded-md max-h-40 overflow-auto divide-y divide-red-100">
                  {result.error_rows.map((r) => (
                    <div key={`e-${r.row}`} className="px-3 py-2 text-xs">
                      <span className="font-mono text-red-700">row {r.row}</span>
                      {r.admission_no && <span className="font-mono text-slate-700"> · {r.admission_no}</span>}
                      <span className="text-red-700"> — {r.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.skipped_rows?.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Skipped</div>
                <div className="border border-amber-200 bg-amber-50 rounded-md max-h-40 overflow-auto divide-y divide-amber-100">
                  {result.skipped_rows.map((r) => (
                    <div key={`s-${r.row}`} className="px-3 py-2 text-xs">
                      <span className="font-mono text-amber-800">row {r.row}</span>
                      {r.admission_no && <span className="font-mono text-slate-700"> · {r.admission_no}</span>}
                      <span className="text-amber-800"> — {r.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={reset} data-testid="bulk-import-another-btn">
                Import another file
              </Button>
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-blue-900 hover:bg-blue-800"
                data-testid="bulk-import-done-btn"
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResultCounter({ label, value, tone, icon }) {
  const tones = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    red: "bg-red-50 border-red-200 text-red-800",
  };
  return (
    <div className={`rounded-md border px-3 py-2 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold opacity-80">
        {icon} {label}
      </div>
      <div className="text-2xl font-bold font-heading mt-1 font-mono">{value}</div>
    </div>
  );
}
